/**
 * @zendir/ui - Realistic Space Data Generators
 *
 * Generate realistic demo data for space-specific charts.
 * All generators produce physically accurate data following orbital mechanics
 * and RF/signal propagation principles.
 */

import type {
  PowerChartData,
  AttitudeChartData,
  WaterfallDataPoint,
  DopplerTrackPoint,
  SatellitePassInfo,
  SpectrumDataPoint,
  ContactWindowData,
  EclipseTimelineData,
  HeliocentricOrbitData,
} from "./types";
import type { ConjunctionEvent as _ConjunctionEvent } from "./domain";

// =============================================================================
// Constants
// =============================================================================

const SPEED_OF_LIGHT = 299792458; // m/s
const EARTH_RADIUS = 6371; // km
const MU_EARTH = 398600.4418; // km³/s² (Earth gravitational parameter)

// =============================================================================
// Power System Generator
// =============================================================================

export interface PowerGeneratorConfig {
  /** Orbit period in seconds (default: 5400 = 90 min LEO) */
  orbitPeriod?: number;
  /** Eclipse duration in seconds (default: 2100 = 35 min) */
  eclipseDuration?: number;
  /** Solar array power output at optimal pointing (W) */
  solarArrayPower?: number;
  /** Average load power (W) */
  loadPower?: number;
  /** Battery capacity (Wh) */
  batteryCapacity?: number;
  /** Initial state of charge (%) */
  initialSoC?: number;
  /** Load reduction factor during eclipse (0-1) */
  eclipseLoadReduction?: number;
  /** Simulation duration in orbits */
  orbits?: number;
  /** Time resolution in seconds */
  resolution?: number;
}

/**
 * Generate realistic LEO satellite power system telemetry
 *
 * Models:
 * - Solar array power variation with orbit phase (cosine pointing loss)
 * - Eclipse periods with zero solar input
 * - Battery charge/discharge with 90% round-trip efficiency
 * - Load shedding during eclipse
 */
export function generatePowerData(
  config: PowerGeneratorConfig = {},
): PowerChartData[] {
  const {
    orbitPeriod = 5400,
    eclipseDuration = 2100,
    solarArrayPower = 85,
    loadPower = 45,
    batteryCapacity = 300,
    initialSoC = 80,
    eclipseLoadReduction = 0.7,
    orbits = 2,
    resolution = 60,
  } = config;

  const data: PowerChartData[] = [];
  const totalDuration = orbitPeriod * orbits;
  let batterySoC = initialSoC;
  const batteryEfficiency = 0.9;

  // Eclipse starts at 40% of orbit, ends at 78% (typical LEO)
  const eclipseStart = 0.4;
  const eclipseEnd = eclipseStart + eclipseDuration / orbitPeriod;

  for (let t = 0; t < totalDuration; t += resolution) {
    const orbitPhase = (t % orbitPeriod) / orbitPeriod;
    const inEclipse = orbitPhase >= eclipseStart && orbitPhase < eclipseEnd;

    // Solar power with pointing variation (±10% sinusoidal)
    const pointingFactor = 0.9 + 0.1 * Math.sin(orbitPhase * Math.PI * 2);
    const solarPower = inEclipse ? 0 : solarArrayPower * pointingFactor;

    // Load power (reduced during eclipse for power conservation)
    const consumption = inEclipse
      ? loadPower * eclipseLoadReduction
      : loadPower;

    // Net power (positive = charging, negative = discharging)
    const netPower = solarPower - consumption;

    // Battery state of charge update
    const energyDelta = (netPower * resolution) / 3600; // Wh
    const efficiencyFactor =
      netPower > 0 ? batteryEfficiency : 1 / batteryEfficiency;
    batterySoC += ((energyDelta * efficiencyFactor) / batteryCapacity) * 100;
    batterySoC = Math.max(0, Math.min(100, batterySoC));

    data.push({
      time: Date.now() + t * 1000,
      solarPower: Math.round(solarPower * 10) / 10,
      consumption: Math.round(consumption * 10) / 10,
      netPower: Math.round(netPower * 10) / 10,
      batteryPercent: Math.round(batterySoC * 10) / 10,
      eclipse: inEclipse,
    });
  }

  return data;
}

// =============================================================================
// Attitude Generator
// =============================================================================

export interface AttitudeGeneratorConfig {
  /** Initial attitude [roll, pitch, yaw] in degrees */
  initialAttitude?: [number, number, number];
  /** Target attitude [roll, pitch, yaw] in degrees */
  targetAttitude?: [number, number, number];
  /** Slew rate in deg/s */
  slewRate?: number;
  /** Settling time in seconds */
  settleTime?: number;
  /** Pointing jitter RMS in degrees */
  jitter?: number;
  /** Sampling rate in Hz */
  samplingRate?: number;
  /** Duration in seconds */
  duration?: number;
}

/**
 * Generate realistic spacecraft attitude telemetry
 *
 * Models:
 * - Slew maneuver with trapezoidal velocity profile
 * - Fine pointing settling with exponential decay
 * - Random jitter (Gaussian distribution)
 * - Angular velocity from finite differences
 */
export function generateAttitudeData(
  config: AttitudeGeneratorConfig = {},
): AttitudeChartData[] {
  const {
    initialAttitude = [0, -45, 90],
    targetAttitude = [0, 30, 180],
    slewRate = 0.5,
    settleTime = 30,
    jitter = 0.02,
    samplingRate = 1,
    duration = 300,
  } = config;

  const data: AttitudeChartData[] = [];
  const dt = 1 / samplingRate;

  // Calculate slew geometry
  const deltaAttitude = targetAttitude.map((t, i) => t - initialAttitude[i]);
  const totalAngle = Math.sqrt(
    deltaAttitude.reduce((sum, d) => sum + d * d, 0),
  );
  const slewDuration = totalAngle / slewRate;
  const _unitVector = deltaAttitude.map((d) => d / totalAngle);

  // Gaussian random for jitter
  const gaussRandom = () => {
    const u1 = Math.random();
    const u2 = Math.random();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  };

  let prevRoll = initialAttitude[0];
  let prevPitch = initialAttitude[1];
  let prevYaw = initialAttitude[2];

  for (let t = 0; t < duration; t += dt) {
    let roll: number, pitch: number, yaw: number;
    let pointingError: number;

    if (t < slewDuration) {
      // During slew
      const progress = t / slewDuration;
      const smoothProgress = 0.5 - 0.5 * Math.cos(progress * Math.PI); // Smooth ease
      roll = initialAttitude[0] + deltaAttitude[0] * smoothProgress;
      pitch = initialAttitude[1] + deltaAttitude[1] * smoothProgress;
      yaw = initialAttitude[2] + deltaAttitude[2] * smoothProgress;
      pointingError = totalAngle * (1 - progress);
    } else if (t < slewDuration + settleTime) {
      // Settling phase
      const settleProgress = (t - slewDuration) / settleTime;
      const decayFactor = Math.exp(-3 * settleProgress);
      const overshoot =
        0.05 * Math.sin(settleProgress * Math.PI * 4) * decayFactor;

      roll = targetAttitude[0] + overshoot + jitter * gaussRandom();
      pitch = targetAttitude[1] + overshoot + jitter * gaussRandom();
      yaw = targetAttitude[2] + overshoot + jitter * gaussRandom();
      pointingError = Math.abs(overshoot) + jitter * Math.abs(gaussRandom());
    } else {
      // Fine pointing
      roll = targetAttitude[0] + jitter * gaussRandom();
      pitch = targetAttitude[1] + jitter * gaussRandom();
      yaw = targetAttitude[2] + jitter * gaussRandom();
      pointingError =
        jitter *
        Math.sqrt(
          Math.pow(gaussRandom(), 2) +
            Math.pow(gaussRandom(), 2) +
            Math.pow(gaussRandom(), 2),
        );
    }

    // Angular velocity from finite differences
    const angularVelocity = Math.sqrt(
      Math.pow((roll - prevRoll) / dt, 2) +
        Math.pow((pitch - prevPitch) / dt, 2) +
        Math.pow((yaw - prevYaw) / dt, 2),
    );

    data.push({
      time: Date.now() + t * 1000,
      roll: Math.round(roll * 100) / 100,
      pitch: Math.round(pitch * 100) / 100,
      yaw: Math.round(yaw * 100) / 100,
      pointingError: Math.round(pointingError * 1000) / 1000,
      angularVelocity: Math.round(angularVelocity * 1000) / 1000,
    });

    prevRoll = roll;
    prevPitch = pitch;
    prevYaw = yaw;
  }

  return data;
}

// =============================================================================
// Doppler Track Generator
// =============================================================================

export interface DopplerGeneratorConfig {
  /** Carrier frequency in Hz */
  carrierFrequency?: number;
  /** Satellite altitude in km */
  altitude?: number;
  /** Maximum elevation angle in degrees */
  maxElevation?: number;
  /** Pass duration in seconds */
  passDuration?: number;
  /** Time resolution in seconds */
  resolution?: number;
  /** Add measurement noise (Hz RMS) */
  measurementNoise?: number;
  /** Satellite name */
  satelliteName?: string;
  /** Ground station name */
  groundStation?: string;
}

/**
 * Generate realistic Doppler tracking data for a satellite pass
 *
 * Uses simplified orbital mechanics:
 * - Circular orbit at specified altitude
 * - Pass geometry from maximum elevation
 * - Doppler shift from velocity projection
 */
export function generateDopplerData(config: DopplerGeneratorConfig = {}): {
  predicted: DopplerTrackPoint[];
  measured: DopplerTrackPoint[];
  passInfo: SatellitePassInfo;
} {
  const {
    carrierFrequency = 437e6, // 437 MHz (UHF amateur)
    altitude = 400, // km (ISS altitude)
    maxElevation = 75,
    passDuration = 600, // 10 minutes
    resolution = 1,
    measurementNoise = 50, // Hz
    satelliteName = "ISS",
    groundStation: _groundStation = "Home Station",
  } = config;

  const predicted: DopplerTrackPoint[] = [];
  const measured: DopplerTrackPoint[] = [];

  // Orbital velocity
  const orbitRadius = EARTH_RADIUS + altitude;
  const orbitalVelocity = Math.sqrt(MU_EARTH / orbitRadius); // km/s

  // Maximum Doppler shift
  const maxDoppler =
    carrierFrequency * ((orbitalVelocity * 1000) / SPEED_OF_LIGHT);

  // Calculate pass geometry
  const elevationRad = (maxElevation * Math.PI) / 180;
  const passHalfAngle = Math.PI / 2 - elevationRad;

  const now = Date.now();
  const aos = now;
  const los = now + passDuration * 1000;
  const tca = now + (passDuration / 2) * 1000;

  // Gaussian random for noise
  const gaussRandom = () => {
    const u1 = Math.random();
    const u2 = Math.random();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  };

  for (let t = 0; t <= passDuration; t += resolution) {
    const time = now + t * 1000;
    const passPhase = t / passDuration; // 0 to 1
    const angle = (passPhase - 0.5) * 2 * passHalfAngle; // -halfAngle to +halfAngle

    // Doppler shift (maximum at edges, zero at TCA)
    const dopplerShift = maxDoppler * Math.sin(angle);

    // Elevation angle (maximum at TCA)
    const elevation = maxElevation * Math.cos(angle);

    predicted.push({
      time,
      dopplerShift: Math.round(dopplerShift),
      elevation: Math.round(elevation * 10) / 10,
    });

    // Simulated measured data with noise and bias
    const measuredShift = dopplerShift + measurementNoise * gaussRandom();
    measured.push({
      time,
      dopplerShift: Math.round(measuredShift),
      elevation: Math.round(elevation * 10) / 10,
    });
  }

  const passInfo: SatellitePassInfo = {
    name: satelliteName,
    aos: new Date(aos),
    los: new Date(los),
    tca: new Date(tca),
    maxElevation,
  };

  return { predicted, measured, passInfo };
}

// =============================================================================
// Waterfall/Spectrum Generator
// =============================================================================

export interface WaterfallGeneratorConfig {
  /** Center frequency in Hz */
  centerFrequency?: number;
  /** Bandwidth in Hz */
  bandwidth?: number;
  /** Number of FFT bins */
  fftBins?: number;
  /** Noise floor in dBm */
  noiseFloor?: number;
  /** Number of time samples */
  timeSamples?: number;
  /** Signals to include */
  signals?: Array<{
    /** Offset from center frequency (Hz) */
    offset: number;
    /** Signal power (dBm) */
    power: number;
    /** Signal bandwidth (Hz) */
    bandwidth: number;
    /** Signal type */
    type: "cw" | "ssb" | "digital" | "beacon";
    /** Doppler drift (Hz/s) - for moving signals */
    dopplerDrift?: number;
  }>;
}

/**
 * Generate realistic RF waterfall data
 *
 * Models:
 * - Gaussian noise floor
 * - CW signals (narrow spikes)
 * - SSB signals (offset sideband with modulation)
 * - Digital signals (wider, flatter spectrum)
 * - Time-varying Doppler drift
 */
export function generateWaterfallData(
  config: WaterfallGeneratorConfig = {},
): WaterfallDataPoint[] {
  const {
    centerFrequency = 437e6,
    bandwidth = 100e3,
    fftBins = 512,
    noiseFloor = -120,
    timeSamples = 100,
    signals = [
      { offset: -30e3, power: -80, bandwidth: 500, type: "cw" as const },
      { offset: 0, power: -75, bandwidth: 20e3, type: "digital" as const },
      { offset: 25e3, power: -90, bandwidth: 3e3, type: "ssb" as const },
    ],
  } = config;

  const data: WaterfallDataPoint[] = [];
  const freqStep = bandwidth / fftBins;
  const noiseStdDev = 3; // dB variation

  // Gaussian random
  const gaussRandom = () => {
    const u1 = Math.random();
    const u2 = Math.random();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  };

  // Pre-compute frequency bins (constant for all time slices)
  const frequencies: number[] = [];
  for (let f = 0; f < fftBins; f++) {
    frequencies.push(centerFrequency + (f - fftBins / 2) * freqStep);
  }

  for (let t = 0; t < timeSamples; t++) {
    const powers: number[] = [];
    const time = Date.now() - (timeSamples - t) * 100; // 100ms between samples

    for (let f = 0; f < fftBins; f++) {
      const freqOffset = (f - fftBins / 2) * freqStep;
      let power = noiseFloor + noiseStdDev * gaussRandom();

      // Add signals
      for (const signal of signals) {
        const signalOffset =
          signal.offset + (signal.dopplerDrift || 0) * (t / 10);
        const distance = Math.abs(freqOffset - signalOffset);

        if (signal.type === "cw") {
          // Narrow CW signal (sinc-like)
          if (distance < signal.bandwidth * 2) {
            const shape = Math.pow(
              Math.sin((Math.PI * distance) / signal.bandwidth) /
                ((Math.PI * distance) / signal.bandwidth + 0.001),
              2,
            );
            power = Math.max(
              power,
              signal.power + 10 * Math.log10(shape + 0.001) + gaussRandom(),
            );
          }
        } else if (signal.type === "ssb") {
          // SSB with voice-like modulation
          if (
            freqOffset >= signalOffset &&
            freqOffset <= signalOffset + signal.bandwidth
          ) {
            const modulation = 0.5 + 0.5 * Math.sin(t * 0.3 + f * 0.1);
            power = Math.max(
              power,
              signal.power - 3 + 6 * modulation + 2 * gaussRandom(),
            );
          }
        } else if (signal.type === "digital") {
          // Digital signal (flat-topped with rolloff)
          if (distance < signal.bandwidth / 2) {
            power = Math.max(power, signal.power + gaussRandom());
          } else if (distance < signal.bandwidth) {
            const rolloff =
              1 - (distance - signal.bandwidth / 2) / (signal.bandwidth / 2);
            power = Math.max(
              power,
              signal.power - 20 * (1 - rolloff) + gaussRandom(),
            );
          }
        } else if (signal.type === "beacon") {
          // Beacon (stable CW)
          if (distance < signal.bandwidth) {
            power = Math.max(power, signal.power);
          }
        }
      }

      powers.push(Math.round(power * 10) / 10);
    }

    data.push({ time, frequencies: [...frequencies], powers });
  }

  return data;
}

// =============================================================================
// Contact Window Generator
// =============================================================================

export interface ContactGeneratorConfig {
  /** Ground stations */
  groundStations?: Array<{
    name: string;
    latitude: number;
    longitude: number;
  }>;
  /** Satellite altitude (km) */
  altitude?: number;
  /** Orbit inclination (degrees) */
  inclination?: number;
  /** Simulation duration (hours) */
  duration?: number;
  /** Minimum elevation (degrees) */
  minElevation?: number;
}

/**
 * Generate realistic ground station contact windows
 *
 * Uses simplified visibility calculation based on:
 * - Orbital period from altitude
 * - Swath width from elevation angle
 * - Ground track precession
 */
export function generateContactWindows(
  config: ContactGeneratorConfig = {},
): ContactWindowData[] {
  const {
    groundStations = [
      { name: "Svalbard (SG)", latitude: 78.2, longitude: 15.4 },
      { name: "McMurdo (MCM)", latitude: -77.8, longitude: 166.7 },
      { name: "Hawaii (HBK)", latitude: 21.5, longitude: -158.0 },
      { name: "Kiruna (KIR)", latitude: 67.9, longitude: 20.9 },
    ],
    altitude = 400,
    inclination = 51.6,
    duration = 24,
    minElevation = 5,
  } = config;

  const result: ContactWindowData[] = [];
  const now = Date.now();
  const orbitPeriod =
    2 * Math.PI * Math.sqrt(Math.pow(EARTH_RADIUS + altitude, 3) / MU_EARTH);
  const passesPerDay = Math.floor((duration * 3600) / orbitPeriod);

  for (const gs of groundStations) {
    const windows: ContactWindowData["windows"] = [];

    // Determine visibility based on latitude and inclination
    const isVisible = Math.abs(gs.latitude) <= inclination + 15;
    if (!isVisible) continue;

    // Generate passes based on ground station latitude
    const passesForStation = Math.floor(
      passesPerDay * (0.3 + 0.7 * Math.cos((gs.latitude * Math.PI) / 180)),
    );

    for (let i = 0; i < passesForStation; i++) {
      const passStart = now + (i * duration * 3600 * 1000) / passesForStation;
      const passStart2 = passStart + (Math.random() - 0.5) * orbitPeriod * 500; // Jitter

      // Pass duration depends on elevation (higher el = shorter duration but better signal)
      const maxEl = minElevation + Math.random() * (85 - minElevation);
      const passDuration = 10 * 60 * 1000 * (1 - maxEl / 180); // 5-10 minutes

      const signalQuality =
        maxEl > 45 ? "good" : maxEl > 20 ? "marginal" : "poor";

      windows.push({
        start: passStart2,
        end: passStart2 + passDuration,
        label: `Pass ${i + 1}`,
        signalQuality: signalQuality as "good" | "marginal" | "poor",
        elevation: Math.round(maxEl),
      });
    }

    result.push({ id: gs.name, name: gs.name, windows });
  }

  return result;
}

// =============================================================================
// Heliocentric Orbit Generator
// =============================================================================

export interface HeliocentricGeneratorConfig {
  /** Bodies to include */
  bodies?: Array<{
    name: string;
    type: "planet" | "spacecraft" | "asteroid" | "comet";
    semiMajorAxis: number; // AU
    eccentricity: number;
    inclination: number; // degrees
    argumentOfPerihelion: number; // degrees
    trueAnomaly: number; // degrees (current position)
    color?: string;
  }>;
  /** Number of points per orbit */
  orbitPoints?: number;
}

/**
 * Generate heliocentric orbit visualization data
 */
export function generateHeliocentricOrbits(
  config: HeliocentricGeneratorConfig = {},
): HeliocentricOrbitData[] {
  const {
    bodies = [
      {
        name: "Earth",
        type: "planet" as const,
        semiMajorAxis: 1.0,
        eccentricity: 0.017,
        inclination: 0,
        argumentOfPerihelion: 102,
        trueAnomaly: 0,
        color: "#3498db",
      },
      {
        name: "Mars",
        type: "planet" as const,
        semiMajorAxis: 1.52,
        eccentricity: 0.093,
        inclination: 1.85,
        argumentOfPerihelion: 286,
        trueAnomaly: 45,
        color: "#e74c3c",
      },
      {
        name: "Voyager 1",
        type: "spacecraft" as const,
        semiMajorAxis: 40,
        eccentricity: 0.99,
        inclination: 35,
        argumentOfPerihelion: 0,
        trueAnomaly: 170,
        color: "#3E3CFF",
      },
    ],
    orbitPoints = 72,
  } = config;

  return bodies.map((body) => {
    const {
      semiMajorAxis: a,
      eccentricity: e,
      inclination: i,
      argumentOfPerihelion: omega,
      trueAnomaly: nu,
    } = body;

    // Generate orbit path
    const orbitPath: { x: number; y: number; z: number }[] = [];
    for (let j = 0; j <= orbitPoints; j++) {
      const angle = (j / orbitPoints) * 2 * Math.PI;
      const r = (a * (1 - e * e)) / (1 + e * Math.cos(angle));

      // Rotate by argument of perihelion and inclination
      const xOrbit = r * Math.cos(angle);
      const yOrbit = r * Math.sin(angle);

      const omegaRad = (omega * Math.PI) / 180;
      const iRad = (i * Math.PI) / 180;

      const x =
        xOrbit * Math.cos(omegaRad) -
        yOrbit * Math.cos(iRad) * Math.sin(omegaRad);
      const y =
        xOrbit * Math.sin(omegaRad) +
        yOrbit * Math.cos(iRad) * Math.cos(omegaRad);
      const z = yOrbit * Math.sin(iRad);

      orbitPath.push({ x, y, z });
    }

    // Current position
    const nuRad = (nu * Math.PI) / 180;
    const r = (a * (1 - e * e)) / (1 + e * Math.cos(nuRad));
    const xOrbit = r * Math.cos(nuRad);
    const yOrbit = r * Math.sin(nuRad);

    const omegaRad = (omega * Math.PI) / 180;
    const iRad = (i * Math.PI) / 180;

    const x =
      xOrbit * Math.cos(omegaRad) -
      yOrbit * Math.cos(iRad) * Math.sin(omegaRad);
    const y =
      xOrbit * Math.sin(omegaRad) +
      yOrbit * Math.cos(iRad) * Math.cos(omegaRad);
    const z = yOrbit * Math.sin(iRad);

    // Orbital period in days
    const period = 365.25 * Math.pow(a, 1.5);

    return {
      name: body.name,
      type: body.type,
      x,
      y,
      z,
      orbitPath,
      color: body.color,
      semiMajorAxis: a,
      period,
      inclination: i,
      size: body.type === "planet" ? 20 : 12,
    };
  });
}

// =============================================================================
// Spectrum Generator (single FFT snapshot)
// =============================================================================

export interface SpectrumGeneratorConfig {
  /** Center frequency (Hz) */
  centerFrequency?: number;
  /** Bandwidth (Hz) */
  bandwidth?: number;
  /** Number of points */
  points?: number;
  /** Noise floor (dBm) */
  noiseFloor?: number;
  /** Signals */
  signals?: Array<{
    frequency: number;
    power: number;
    bandwidth: number;
  }>;
}

/**
 * Generate a single RF spectrum snapshot
 */
export function generateSpectrum(
  config: SpectrumGeneratorConfig = {},
): SpectrumDataPoint[] {
  const {
    centerFrequency = 2200e6,
    bandwidth = 10e6,
    points = 256,
    noiseFloor = -120,
    signals = [
      { frequency: 2200e6, power: -75, bandwidth: 100e3 },
      { frequency: 2197.5e6, power: -80, bandwidth: 10e3 },
      { frequency: 2202.5e6, power: -95, bandwidth: 50e3 },
    ],
  } = config;

  const data: SpectrumDataPoint[] = [];
  const freqMin = centerFrequency - bandwidth / 2;
  const freqStep = bandwidth / points;

  for (let i = 0; i < points; i++) {
    const freq = freqMin + i * freqStep;
    let power = noiseFloor + 3 * (Math.random() - 0.5);

    for (const signal of signals) {
      const distance = Math.abs(freq - signal.frequency);
      if (distance < signal.bandwidth * 2) {
        const attenuation =
          20 * Math.log10(1 + distance / (signal.bandwidth / 2));
        power = Math.max(power, signal.power - attenuation);
      }
    }

    data.push({ frequency: freq, power: Math.round(power * 10) / 10 });
  }

  return data;
}

// =============================================================================
// Eclipse Timeline Generator
// =============================================================================

export interface EclipseGeneratorConfig {
  /** Orbit period (seconds) */
  orbitPeriod?: number;
  /** Eclipse fraction (0-1) */
  eclipseFraction?: number;
  /** Number of orbits */
  orbits?: number;
  /** Include penumbra */
  includePenumbra?: boolean;
}

/**
 * Generate eclipse timeline data
 */
export function generateEclipseTimeline(
  config: EclipseGeneratorConfig = {},
): EclipseTimelineData[] {
  const {
    orbitPeriod = 5400,
    eclipseFraction = 0.38,
    orbits = 5,
    includePenumbra = true,
  } = config;

  const data: EclipseTimelineData[] = [];
  const now = Date.now();
  const penumbraDuration = includePenumbra ? 60 : 0; // 1 minute penumbra

  for (let orbit = 0; orbit < orbits; orbit++) {
    const orbitStart = now + orbit * orbitPeriod * 1000;
    const eclipseStart = orbitStart + orbitPeriod * 0.4 * 1000;
    const eclipseEnd = eclipseStart + orbitPeriod * eclipseFraction * 1000;

    // Sunlight before eclipse
    data.push({
      start: orbitStart,
      end: eclipseStart - penumbraDuration * 1000,
      type: "sunlight",
      orbitNumber: orbit + 1,
    });

    // Penumbra entry
    if (includePenumbra) {
      data.push({
        start: eclipseStart - penumbraDuration * 1000,
        end: eclipseStart,
        type: "penumbra",
        orbitNumber: orbit + 1,
      });
    }

    // Umbra
    data.push({
      start: eclipseStart,
      end: eclipseEnd,
      type: "umbra",
      orbitNumber: orbit + 1,
    });

    // Penumbra exit
    if (includePenumbra) {
      data.push({
        start: eclipseEnd,
        end: eclipseEnd + penumbraDuration * 1000,
        type: "penumbra",
        orbitNumber: orbit + 1,
      });
    }

    // Sunlight after eclipse
    data.push({
      start: eclipseEnd + penumbraDuration * 1000,
      end: orbitStart + orbitPeriod * 1000,
      type: "sunlight",
      orbitNumber: orbit + 1,
    });
  }

  return data;
}

// =============================================================================
// Constellation Coverage Generator
// =============================================================================

export interface CoverageGeneratorConfig {
  /** Latitude resolution (degrees) */
  latResolution?: number;
  /** Longitude resolution (degrees) */
  lonResolution?: number;
  /** Number of orbital planes */
  planes?: number;
  /** Satellites per plane */
  satsPerPlane?: number;
  /** Orbit altitude (km) */
  altitude?: number;
  /** Orbit inclination (degrees) */
  inclination?: number;
  /** Metric to compute: 'visibility' | 'revisitTime' | 'latency' */
  metric?: "visibility" | "revisitTime" | "latency";
}

/**
 * Generate constellation coverage grid data
 *
 * Models simplified visibility based on satellite altitude
 * and ground footprint overlap from Walker constellation geometry.
 */
export function generateConstellationCoverage(
  config: CoverageGeneratorConfig = {},
): {
  latitudes: number[];
  longitudes: number[];
  coverage: number[][];
  unit: string;
} {
  const {
    latResolution = 5,
    lonResolution = 5,
    planes = 6,
    satsPerPlane = 22,
    altitude = 550,
    inclination = 53,
    metric = "visibility",
  } = config;

  const latitudes: number[] = [];
  const longitudes: number[] = [];
  const coverage: number[][] = [];

  for (let lat = -90; lat <= 90; lat += latResolution) latitudes.push(lat);
  for (let lon = -180; lon < 180; lon += lonResolution) longitudes.push(lon);

  // Footprint half-angle from altitude
  const earthRadius = 6371;
  const footprintAngle =
    Math.acos(earthRadius / (earthRadius + altitude)) * (180 / Math.PI);
  const totalSats = planes * satsPerPlane;

  for (let li = 0; li < latitudes.length; li++) {
    const row: number[] = [];
    const lat = latitudes[li];
    const _latRad = (lat * Math.PI) / 180;

    for (let lj = 0; lj < longitudes.length; lj++) {
      let value: number;

      // Coverage depends on latitude vs inclination
      const latFactor =
        Math.abs(lat) <= inclination
          ? 1.0
          : Math.max(0, 1.0 - (Math.abs(lat) - inclination) / 20);

      // Base coverage from constellation density
      const densityFactor = totalSats / 100;
      const footprintOverlap = Math.min(
        1,
        densityFactor * (footprintAngle / 30),
      );

      if (metric === "visibility") {
        // Percentage of time with at least 1 satellite visible
        value = Math.min(100, footprintOverlap * latFactor * 100);
        // Add polar convergence bonus
        if (Math.abs(lat) > 60) value = Math.min(100, value * 1.2);
        // Add noise
        value += (Math.random() - 0.5) * 5;
        value = Math.max(0, Math.min(100, value));
      } else if (metric === "revisitTime") {
        // Minutes between passes (lower is better)
        const baseRevisit = 90 / (footprintOverlap * latFactor + 0.01);
        value = Math.max(
          1,
          Math.min(120, baseRevisit + (Math.random() - 0.5) * 10),
        );
      } else {
        // Latency in ms (one-way)
        const slantRange =
          altitude / Math.sin(Math.max(0.1, (latFactor * Math.PI) / 4));
        value = slantRange / 300 + 2 + Math.random() * 2; // ms
      }

      row.push(Math.round(value * 10) / 10);
    }
    coverage.push(row);
  }

  const units: Record<string, string> = {
    visibility: "%",
    revisitTime: "min",
    latency: "ms",
  };
  return { latitudes, longitudes, coverage, unit: units[metric] || "%" };
}

// =============================================================================
// Conjunction Event Generator
// =============================================================================

export interface ConjunctionGeneratorConfig {
  /** Primary object name */
  primaryName?: string;
  /** Number of events to generate */
  eventCount?: number;
  /** Time range in hours */
  timeRangeHours?: number;
  /** Include high-risk events */
  includeHighRisk?: boolean;
}

/**
 * Generate realistic conjunction/close approach events
 */
export function generateConjunctionEvents(
  config: ConjunctionGeneratorConfig = {},
): Array<{
  id: string;
  primary: string;
  secondary: string;
  tca: number;
  missDistance: number;
  probability: number;
  relativeVelocity: number;
  position: { x: number; y: number; z: number };
  uncertainty?: { rx: number; ry: number; rz: number };
}> {
  const {
    primaryName = "SAT-ALPHA",
    eventCount = 12,
    timeRangeHours = 72,
    includeHighRisk = true,
  } = config;

  const secondaries = [
    "COSMOS-2251 DEB",
    "FENGYUN-1C DEB",
    "SL-16 R/B",
    "IRIDIUM 33 DEB",
    "NOAA 17 DEB",
    "ATLAS 5 CENTAUR R/B",
    "BREEZE-M DEB",
    "CZ-3B R/B",
    "UNKNOWN DEB",
    "MICROSAT-R DEB",
    "DELTA 2 R/B",
    "H-2A R/B",
  ];

  const now = Date.now();
  const events = [];

  for (let i = 0; i < eventCount; i++) {
    const tca = now + Math.random() * timeRangeHours * 3600000;
    let missDistance: number;

    if (includeHighRisk && i < 2) {
      missDistance = 0.1 + Math.random() * 0.9; // Critical: < 1 km
    } else if (i < 5) {
      missDistance = 1 + Math.random() * 4; // Warning: 1-5 km
    } else {
      missDistance = 5 + Math.random() * 15; // Safe: > 5 km
    }

    const probability = Math.pow(10, -3 - missDistance * 1.5 + Math.random());
    const relativeVelocity = 5 + Math.random() * 12;

    const angle = Math.random() * 2 * Math.PI;
    const incAngle = (Math.random() - 0.5) * Math.PI;
    const radius = 6800 + Math.random() * 600;

    events.push({
      id: `CDM-${String(i + 1).padStart(3, "0")}`,
      primary: primaryName,
      secondary: secondaries[i % secondaries.length],
      tca,
      missDistance: Math.round(missDistance * 1000) / 1000,
      probability: Math.max(1e-10, probability),
      relativeVelocity: Math.round(relativeVelocity * 100) / 100,
      position: {
        x: Math.round(radius * Math.cos(angle) * Math.cos(incAngle)),
        y: Math.round(radius * Math.sin(angle) * Math.cos(incAngle)),
        z: Math.round(radius * Math.sin(incAngle)),
      },
      uncertainty: {
        rx: 20 + Math.random() * 100,
        ry: 50 + Math.random() * 300,
        rz: 10 + Math.random() * 60,
      },
    });
  }

  return events.sort((a, b) => a.tca - b.tca);
}

export default {
  generatePowerData,
  generateAttitudeData,
  generateDopplerData,
  generateWaterfallData,
  generateContactWindows,
  generateHeliocentricOrbits,
  generateSpectrum,
  generateEclipseTimeline,
  generateConstellationCoverage,
  generateConjunctionEvents,
};
